import React, { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";

import { Modal, Input, Select, Button } from "../ui";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import WhatsappSelect from "../WhatsappSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";

// Icons
const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const EyeOffIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
    />
  </svg>
);

const schema = yup.object().shape({
  name: yup
    .string()
    .min(2, "Nome muito curto")
    .max(50, "Nome muito longo")
    .required("Nome é obrigatório"),
  password: yup
    .string()
    .test(
      "password-length",
      "Senha muito curta (mínimo 5 caracteres)",
      (value) => !value || value.length === 0 || value.length >= 5
    )
    .test(
      "password-max",
      "Senha muito longa",
      (value) => !value || value.length === 0 || value.length <= 50
    ),
  email: yup
    .string()
    .email("Email inválido")
    .required("Email é obrigatório"),
});

const UserModal = ({ open, onClose, userId }) => {
  const { user: loggedInUser } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [profile, setProfile] = useState("user");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    const fetchUser = async () => {
      if (!open) return;

      if (!userId) {
        reset({
          name: "",
          email: "",
          password: "",
        });
        setSelectedQueueIds([]);
        setSelectedWhatsappIds([]);
        setProfile("user");
        return;
      }

      try {
        const { data } = await api.get(`/users/${userId}`);
        setValue("name", data.name || "");
        setValue("email", data.email || "");
        setValue("password", "");
        setProfile(data.profile || "user");
        const userQueueIds = data.queues?.map((queue) => queue.id) || [];
        setSelectedQueueIds(userQueueIds);
        const userWhatsappIds = data.whatsapps?.map((whatsapp) => whatsapp.id) || [];
        setSelectedWhatsappIds(userWhatsappIds);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open, reset, setValue]);

  const handleClose = () => {
    reset({
      name: "",
      email: "",
      password: "",
    });
    setSelectedQueueIds([]);
    setSelectedWhatsappIds([]);
    setProfile("user");
    onClose();
  };

  const handleSaveUser = async (data) => {
    const userData = {
      ...data,
      profile,
      queueIds: selectedQueueIds,
      whatsappIds: selectedWhatsappIds,
    };

    // Não enviar senha vazia (significa que não quer alterar)
    if (!userData.password) {
      delete userData.password;
    }

    setLoading(true);
    try {
      if (userId) {
        await api.put(`/users/${userId}`, userData);
      } else {
        await api.post("/users", userData);
      }
      toast.success(i18n.t("userModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const profileOptions = [
    { value: "admin", label: "Admin" },
    { value: "user", label: "Usuário" },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        userId
          ? i18n.t("userModal.title.edit")
          : i18n.t("userModal.title.add")
      }
      size="md"
      footer={
        <>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {i18n.t("userModal.buttons.cancel")}
          </Button>
          <Button
            type="submit"
            form="user-form"
            loading={loading}
            disabled={loading}
          >
            {userId
              ? i18n.t("userModal.buttons.okEdit")
              : i18n.t("userModal.buttons.okAdd")}
          </Button>
        </>
      }
    >
      <form
        id="user-form"
        onSubmit={handleSubmit(handleSaveUser)}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={i18n.t("userModal.form.name")}
            {...register("name")}
            error={errors.name?.message}
            autoFocus
            placeholder="Nome do usuário"
          />

          <div className="relative">
            <Input
              label={i18n.t("userModal.form.password")}
              {...register("password")}
              error={errors.password?.message}
              type={showPassword ? "text" : "password"}
              placeholder={userId ? "Deixe em branco para manter" : "Senha"}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors"
                >
                  {showPassword ? (
                    <EyeOffIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={i18n.t("userModal.form.email")}
            {...register("email")}
            error={errors.email?.message}
            type="email"
            placeholder="email@exemplo.com"
          />

          <Can
            role={loggedInUser.profile}
            perform="user-modal:editProfile"
            yes={() => (
              <Select
                label={i18n.t("userModal.form.profile")}
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
                options={profileOptions}
              />
            )}
          />
        </div>

        <Can
          role={loggedInUser.profile}
          perform="user-modal:editQueues"
          yes={() => (
            <QueueSelect
              selectedQueueIds={selectedQueueIds}
              onChange={(values) => setSelectedQueueIds(values)}
            />
          )}
        />

        <Can
          role={loggedInUser.profile}
          perform="user-modal:editQueues"
          yes={() => (
            <WhatsappSelect
              selectedWhatsappIds={selectedWhatsappIds}
              onChange={(values) => setSelectedWhatsappIds(values)}
            />
          )}
        />
      </form>
    </Modal>
  );
};

export default UserModal;
